import uniqBy from "lodash/uniqBy";
import type { ProductResponse } from "../admin/products/productsApi";

export interface ProductWithOptions {
  readonly id: string;
  readonly options: ReadonlyArray<{ readonly id: string; readonly quantity: number }>;
}

export interface ProductBeds {
  readonly productId: string;
  readonly productName: string;
  readonly beds: number;
}

export interface ValidationDiscrepancies {
  readonly participantsNumber: number;
  readonly productBeds: ReadonlyArray<ProductBeds>;
}

export interface ParticipantCountValidationResult {
  readonly valid: boolean;
  readonly discrepancies: ValidationDiscrepancies | undefined;
}

export interface MissingMandatoryOption {
  readonly productId: string;
  readonly productName: string;
  readonly optionName: string;
}

export interface SaveDiscrepancies {
  readonly participantsNumber: number;
  /** Beds of every product requiring a participant list; empty when they all match participantsNumber */
  readonly productBeds: ReadonlyArray<ProductBeds>;
  readonly missingMandatoryOptions: ReadonlyArray<MissingMandatoryOption>;
}

/**
 * Collects everything that makes the selected products inconsistent with the quote before saving: beds not
 * matching the number of participants and mandatory product options that were not selected.
 * Returns undefined when there is nothing to warn about.
 */
export function findSaveDiscrepancies(
  participantsNumber: number,
  products: ReadonlyArray<ProductWithOptions>,
  productDetails: ReadonlyArray<ProductResponse>,
): SaveDiscrepancies | undefined {
  const bedsValidation = validateParticipantsVsBeds(participantsNumber, products, productDetails);
  const missingMandatoryOptions = findMissingMandatoryOptions(products, productDetails);
  if (!bedsValidation.discrepancies && missingMandatoryOptions.length === 0) {
    return undefined;
  }
  return {
    participantsNumber,
    productBeds: bedsValidation.discrepancies?.productBeds ?? [],
    missingMandatoryOptions,
  };
}

/**
 * Finds mandatory options of the selected products that are not selected with a positive quantity.
 * Internal options are skipped, as they cannot be picked by every user. A product selected several times
 * (e.g. on different dates) reports each missing option once.
 */
export function findMissingMandatoryOptions(
  products: ReadonlyArray<ProductWithOptions>,
  productDetails: ReadonlyArray<ProductResponse>,
): MissingMandatoryOption[] {
  const missingOptions = products.flatMap((product) => {
    const details = productDetails.find((p) => p.id === product.id);
    if (!details) {
      return [];
    }
    return details.options
      .filter((option) => option.mandatory && !option.internal)
      .filter((option) => !product.options.some((selected) => selected.id === option.id && selected.quantity > 0))
      .map((option) => ({ productId: details.id, productName: details.name, optionName: option.name }));
  });
  return uniqBy(missingOptions, ({ productId, optionName }) => `${productId}/${optionName}`);
}

/**
 * Validates that participantsNumber matches total beds per product for products that require a participant list.
 * Only options selected in the order are considered: if the order selects no option with participant list for a product,
 * that product is skipped. Products with no selected option requiring participant list are skipped.
 */
export function validateParticipantsVsBeds(
  participantsNumber: number,
  products: ReadonlyArray<ProductWithOptions>,
  productDetails: ReadonlyArray<ProductResponse>,
): ParticipantCountValidationResult {
  const productBeds: ProductBeds[] = products
    .map((product) => ({ product, details: productDetails.find((p) => p.id === product.id) }))
    .filter((entry): entry is { product: ProductWithOptions; details: ProductResponse } => {
      const { product, details } = entry;
      return (
        details !== undefined &&
        product.options.some((orderOpt) => details.options.find((o) => o.id === orderOpt.id)?.participantListRequired)
      );
    })
    .map(({ product, details }) => {
      const beds = sumBedsForProduct(product, details);

      return {
        productId: product.id,
        productName: details.name,
        beds,
      };
    });

  const hasMismatch = productBeds.some((p) => p.beds !== participantsNumber);

  return {
    valid: !hasMismatch,
    discrepancies: hasMismatch && productBeds.length > 0 ? { participantsNumber, productBeds } : undefined,
  };
}

function sumBedsForProduct(product: ProductWithOptions, details: ProductResponse | undefined): number {
  return product.options.reduce((acc, orderOption) => {
    const optionDetails = details?.options.find((o) => o.id === orderOption.id);
    if (!optionDetails?.participantListRequired || !optionDetails.roomConfig) {
      return acc;
    }
    return acc + orderOption.quantity * optionDetails.roomConfig.numberOfPeople;
  }, 0);
}
